import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Button,
  Input,
  message,
  Row,
  Col,
  Tag,
  Divider,
  Icon,
  Alert,
  Modal
} from 'antd';
import moment from 'moment';
import { getAllEquipment } from './utils/maintenanceDB';
import { loadScheduleCsv } from './utils/scheduleCsvLoader';
import { loadAndEnrichEquipment } from './utils/assetListLoader';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

function VehicleTrackingShare({ form: mainForm }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [shareLink, setShareLink] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      // Load vehicles from maintenance tracker data
      let equipment = getAllEquipment();
      
      // If no equipment, try loading from CSV
      if (equipment.length === 0) {
        try {
          let csvEquipment = await loadScheduleCsv('/Schedule Service Tracker.csv');
          csvEquipment = await loadAndEnrichEquipment(csvEquipment);
          setVehicles(csvEquipment);
          return;
        } catch (error) {
          console.warn('Could not load vehicles from CSV:', error);
        }
      }
      
      setVehicles(equipment);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      message.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const generateShareLink = () => {
    if (!selectedVehicle) {
      message.error('Please select a vehicle');
      return;
    }

    if (!dateRange || dateRange.length !== 2) {
      message.error('Please select a date range');
      return;
    }

    // Generate a unique share ID
    const shareId = `share_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Store share data in localStorage
    const shareData = {
      id: shareId,
      vehicleId: selectedVehicle.id,
      vehicleName: selectedVehicle.name,
      unitNo: selectedVehicle.unitNo,
      registration: selectedVehicle.registration,
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      createdAt: new Date().toISOString()
    };

    // Save to localStorage
    const shares = JSON.parse(localStorage.getItem('vehicle_shares') || '[]');
    shares.push(shareData);
    localStorage.setItem('vehicle_shares', JSON.stringify(shares));

    // Generate the shareable link
    const baseUrl = window.location.origin;
    // Use hash-based routing for simplicity (works without React Router)
    const link = `${baseUrl}/#track/${shareId}`;
    setShareLink(link);

    // Generate WhatsApp message
    const startDateStr = dateRange[0].format('DD-MMM-YYYY');
    const endDateStr = dateRange[1].format('DD-MMM-YYYY');
    const msg = `Vehicle Tracking Link\n\nVehicle: ${selectedVehicle.name}\nPeriod: ${startDateStr} to ${endDateStr}\n\nTrack this vehicle:\n${link}`;
    setWhatsappMessage(msg);

    message.success('Shareable link generated successfully!');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('Copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy to clipboard');
    });
  };

  const openWhatsApp = () => {
    const encodedMessage = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const selectedVehicleInfo = vehicles.find(v => v.id === selectedVehicle?.id);

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        title={
          <span>
            <Icon type="share-alt" style={{ marginRight: 8 }} />
            Share Vehicle Tracking (AssetPro GPS)
          </span>
        }
        loading={loading}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Select Vehicle" required>
                <Select
                  placeholder="Choose a vehicle from your fleet"
                  value={selectedVehicle?.id}
                  onChange={(value) => {
                    const vehicle = vehicles.find(v => v.id === value);
                    setSelectedVehicle(vehicle);
                    setShareLink(null);
                    setWhatsappMessage('');
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  size="large"
                >
                  {vehicles.map(vehicle => (
                    <Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.name} {vehicle.registration ? `(${vehicle.registration})` : ''}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Date Range" required>
                <RangePicker
                  style={{ width: '100%' }}
                  size="large"
                  value={dateRange}
                  onChange={(dates) => {
                    setDateRange(dates);
                    setShareLink(null);
                    setWhatsappMessage('');
                  }}
                  format="DD-MMM-YYYY"
                  disabledDate={(current) => current && current > moment().endOf('day')}
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedVehicleInfo && (
            <Alert
              message="Selected Vehicle Information"
              description={
                <div>
                  <p><strong>Name:</strong> {selectedVehicleInfo.name}</p>
                  {selectedVehicleInfo.registration && (
                    <p><strong>Registration:</strong> {selectedVehicleInfo.registration}</p>
                  )}
                  {selectedVehicleInfo.location && (
                    <p><strong>Location:</strong> {selectedVehicleInfo.location}</p>
                  )}
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item>
            <Button
              type="primary"
              size="large"
              icon="link"
              onClick={generateShareLink}
              disabled={!selectedVehicle || !dateRange || dateRange.length !== 2}
            >
              Generate Shareable Link
            </Button>
          </Form.Item>
        </Form>

        {shareLink && (
          <div style={{ marginTop: 24 }}>
            <Divider>Shareable Link</Divider>
            
            <Card style={{ backgroundColor: '#fafafa', marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <strong>Tracking Link:</strong>
              </div>
              <Input.Group compact>
                <Input
                  value={shareLink}
                  readOnly
                  style={{ width: 'calc(100% - 100px)' }}
                />
                <Button
                  icon="copy"
                  onClick={() => copyToClipboard(shareLink)}
                >
                  Copy Link
                </Button>
              </Input.Group>
              <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                This link will show live location and movement history for the selected period
              </div>
            </Card>

            <Card style={{ backgroundColor: '#fafafa', marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <strong>WhatsApp Message Template:</strong>
              </div>
              <TextArea
                value={whatsappMessage}
                readOnly
                rows={6}
                style={{ marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  onClick={openWhatsApp}
                  style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                >
                  <Icon type="message" /> Open WhatsApp
                </Button>
                <Button
                  icon="copy"
                  onClick={() => copyToClipboard(whatsappMessage)}
                >
                  Copy Message
                </Button>
                <Button
                  icon="eye"
                  onClick={() => setPreviewModalVisible(true)}
                >
                  Preview Link
                </Button>
              </div>
            </Card>

            <Alert
              message="Link Details"
              description={
                <div>
                  <p><strong>Vehicle:</strong> {selectedVehicle.name}</p>
                  <p><strong>Period:</strong> {dateRange[0].format('DD-MMM-YYYY')} to {dateRange[1].format('DD-MMM-YYYY')}</p>
                  <p><strong>Link Expires:</strong> {moment(dateRange[1]).add(30, 'days').format('DD-MMM-YYYY')}</p>
                </div>
              }
              type="success"
              showIcon
            />
          </div>
        )}
      </Card>

      {/* Preview Modal */}
      <Modal
        title="Preview Tracking View"
        visible={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="open"
            type="primary"
            onClick={() => {
              window.open(shareLink, '_blank');
              setPreviewModalVisible(false);
            }}
          >
            Open in New Tab
          </Button>
        ]}
        width={800}
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Icon type="environment" style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
          <h3>Vehicle Tracking View</h3>
          <p>This link will display:</p>
          <ul style={{ textAlign: 'left', display: 'inline-block' }}>
            <li>Live location of the vehicle</li>
            <li>Movement history for the selected period</li>
            <li>Route visualization on map</li>
            <li>Speed and distance information</li>
          </ul>
          <div style={{ marginTop: 24 }}>
            <Tag color="blue">Vehicle: {selectedVehicle?.name}</Tag>
            <Tag color="green">
              {dateRange[0]?.format('DD-MMM-YYYY')} to {dateRange[1]?.format('DD-MMM-YYYY')}
            </Tag>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Form.create({ name: 'vehicle_tracking_share' })(VehicleTrackingShare);

